import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { IndexComponent } from './pages/index/index.component';
import { SectionsIndexComponent } from './pages/sections-index/sections.component';

import { NgxUiLoaderModule, NgxUiLoaderConfig, SPINNER, POSITION, PB_DIRECTION } from 'ngx-ui-loader';

import { HttpClientModule } from '@angular/common/http';

import { ReactiveFormsModule } from '@angular/forms';
import { AuthGuard } from './services/core/authguard.service';

import { FormsModule } from '@angular/forms';

import { ModalContentComponent } from './modals/modal-content/modal-content.component';
import { ModalLoginComponent } from './modals/modal-login/modal-login.component';
import { ModalRegisterComponent } from './modals/modal-register/modal-register.component';
import { ModalPdfComponent } from './modals/modal-pdf/modal-pdf.component';
import { ModalAnswersOverviewComponent } from './modals/modal-answers-overview/modal-answers-overview.component';
import { ModalAnswersOverviewStudentComponent } from './modals/modal-answers-overview-student/modal-answers-overview-student.component';
import { ModalAnswersOverviewQuestionComponent } from './modals/modal-answers-overview-question/modal-answers-overview-question.component';
import { ModalConfirmDeleteComponent } from './modals/modal-confirm-delete/modal-confirm-delete.component';
import { ModalPasswordRecoveryComponent } from './modals/modal-password-recovery/modal-password-recovery.component';

import { HasErrorPipe } from './pipes/has-error.pipe';
import { LessonDetailsComponent } from './pages/lesson-details/lesson-details.component';
import { LessonOverviewComponent } from './pages/lesson-overview/lesson-overview.component';

import { AppService } from './services/app.service';
import { ConfigService } from './services/core/config.service';
import { ProfileComponent } from './pages/profile/profile.component';
import { PasswordResetComponent } from './pages/password-reset/password-reset.component';
import { PasswordRecoveryComponent } from './pages/password-recovery/password-recovery.component';

import { CommonModule } from "@angular/common";

import { ElectiveComponent } from './pages/elective-overview/elective.component';
import { PagesComponent } from './pages/pages/pages.component';
import { EntreeComponent } from './pages/entree/entree.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { DebugComponent } from './pages/debug/debug.component';

import { environment } from 'src/environments/environment';
import { ModuleIndexComponent } from './pages/module-index/module-index.component';

import { NgcCookieConsentModule } from 'ngx-cookieconsent';
import { NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { LessonNavBlockComponent } from './components/lesson-nav-block/lesson-nav-block.component';

import { NgbCarouselConfig, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { ModalPortfolioUploadComponent } from './modals/modal-portfolio-upload/modal-portfolio-upload.component';

import { ImageCropperModule } from 'ngx-image-cropper';
import { ModalPortfolioPreviewComponent } from './modals/modal-portfolio-preview/modal-portfolio-preview.component';
import { ModalPortfolioOverviewComponent } from './modals/modal-portfolio-overview/modal-portfolio-overview.component';
import { ModalPortfolioFeedbackComponent } from './modals/modal-portfolio-feedback/modal-portfolio-feedback.component';
import { ModalNameComponent } from './modals/modal-name/modal-name.component';
import { ActivateAccountComponent } from './pages/activate-account/activate-account.component';

registerLocaleData(localeNl);

const cookieConfig: NgcCookieConsentConfig = {
  "cookie": {
    "domain": ((window.location.hostname).split('.')).slice(-2).join('.')
  },
  "position": "bottom",
  "theme": "block",
  "palette": {
    "popup": {
      "background": "#f06793",
      "text": "#ffffff",
      "link": "#ffffff",
    },
    "button": {
      "background": "#000000",
      "text": "#ffffff",
      "border": "transparent"
    }
  },
  "type": "opt-in",
  "content": {
    "message": "Deze website maakt gebruik van cookies om ervoor te zorgen dat je de beste ervaring krijgt.",
    "dismiss": "Got it!",
    "deny": "Weigeren",
    "allow": "Cookies 🍪 Accepteren",
    "link": "Meer informatie",
    "href": "https://uitgeverijneo.nl/files/privacybeleid.pdf",
    "policy": "Cookie Policy"
  }
}

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  "bgsColor": "#5c2d2d",
  "bgsOpacity": 0.5,
  "bgsPosition": "bottom-right",
  "bgsSize": 60,
  "bgsType": "ball-spin-clockwise",
  "blur": 5,
  "delay": 0,
  "fastFadeOut": true,
  "fgsColor": "#001e79",
  "fgsPosition": "center-center",
  "fgsSize": 60,
  "fgsType": "pulse",
  "gap": 24,
  "logoPosition": "center-center",
  "logoSize": 120,
  "logoUrl": "",
  "masterLoaderId": "master",
  "overlayBorderRadius": "0",
  "overlayColor": "rgba(255,255,255,0.8)",
  "pbColor": "red",
  "pbDirection": "ltr",
  "pbThickness": 3,
  "hasProgressBar": false,
  "text": "",
  "textColor": "#FFFFFF",
  "textPosition": "center-center",
  "maxTime": -1,
  "minTime": 300
};

export function appInit(configService: ConfigService) {
  return () => configService.load();
}

export function ssoTokenCheckInit(appService: AppService) {
  return () => appService.checkToken();
}

@NgModule({
  declarations: [
    AppComponent,
    IndexComponent,
    SectionsIndexComponent,
    ElectiveComponent,
    ModalContentComponent,
    ModalConfirmDeleteComponent,
    ModalLoginComponent,
    ModalPasswordRecoveryComponent,
    ModalPdfComponent,
    ModalAnswersOverviewComponent,
    ModalAnswersOverviewStudentComponent,
    ModalAnswersOverviewQuestionComponent,
    ModalRegisterComponent,
    HasErrorPipe,
    LessonDetailsComponent,
    ProfileComponent,
    PasswordResetComponent,
    PasswordRecoveryComponent,
    PagesComponent,
    EntreeComponent,
    NotFoundComponent,
    LessonOverviewComponent,
    DebugComponent,
    ModuleIndexComponent,
    LessonNavBlockComponent,
    ModalPortfolioUploadComponent,
    ModalPortfolioPreviewComponent,
    ModalPortfolioOverviewComponent,
    ModalPortfolioFeedbackComponent,
    ModalNameComponent,
    ActivateAccountComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    ReactiveFormsModule,
    FormsModule,
    AppRoutingModule,
    NgbModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    NgbAccordionModule,
    ImageCropperModule
  ],
  providers: [
    AuthGuard,
    { provide: LOCALE_ID, useValue: "nl" },
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      multi: true,
      deps: [ConfigService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: ssoTokenCheckInit,
      multi: true,
      deps: [AppService]
    }
  ],
  bootstrap: [AppComponent],
  entryComponents: [ModalContentComponent]
})
export class AppModule { }